import React, { useEffect, useState } from "react";
import { api, formatApiError, formatCurrency, formatDate } from "@/lib/api";
import {
  Coins, TrendUp, Receipt, CreditCard, ChartLineUp, Warning, Buildings,
  Users, ShoppingBag, Briefcase, FileText, Bank, CheckCircle,
} from "@phosphor-icons/react";

/**
 * Detailed Project Financial Snapshot panel — replaces / complements the
 * inline ProjectFinanceChip when the project card is expanded.
 *
 * Pulls cross-module data from /api/projects/{id}/financial-snapshot:
 * - Revenue: recognised · invoiced · received · outstanding AR
 * - Costs: direct (finance entries) · procurement · payroll · assets · overhead
 * - Margin: gross $ + % · budget used % · budget remaining
 * - Linked records: invoice count, procurement records, assets, pending approvals
 */
export default function ProjectFinancialSnapshotPanel({ projectId }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!projectId) return;
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const { data: snap } = await api.get(`/projects/${projectId}/financial-snapshot`);
        if (active) setData(snap);
      } catch (e) {
        if (active) setErr(formatApiError(e));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [projectId]);

  if (loading) {
    return (
      <section className="border-t border-slate-100 px-5 py-4" data-testid={`pfs-loading-${projectId}`}>
        <div className="overline mb-2 text-slate-500">Project Financial Snapshot</div>
        <div className="font-mono-alt text-[11px] uppercase tracking-widest text-slate-400">Loading…</div>
      </section>
    );
  }

  if (err) {
    return (
      <section className="border-t border-slate-100 px-5 py-4" data-testid={`pfs-error-${projectId}`}>
        <div className="overline mb-2 text-slate-500">Project Financial Snapshot</div>
        <div className="rounded-md border border-amber-200 bg-amber-50/60 px-3 py-2 text-xs text-amber-800">
          <Warning size={11} weight="fill" className="inline mr-1" /> {err}
        </div>
      </section>
    );
  }

  if (!data) return null;

  // ── Empty state: no finance records anywhere yet ────────────────
  // Backend signals this via `has_any_data:false`. Render a clear
  // empty-state card instead of misleading $0 / $0 / $0 / blank-margin tiles.
  if (data.has_any_data === false) {
    return (
      <section className="border-t border-slate-100 px-5 py-4" data-testid={`pfs-empty-${projectId}`}>
        <div className="overline mb-2 text-slate-500">Project Financial Snapshot</div>
        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50/40 px-5 py-8 text-center">
          <div className="mx-auto mb-2 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-sm">
            <Warning size={18} weight="duotone" className="text-slate-400" />
          </div>
          <div className="font-display text-sm font-semibold text-slate-900">No financial activity yet</div>
          <p className="mt-1 text-xs text-slate-600 max-w-md mx-auto">
            This project has no invoices, procurement, payroll, or asset costs recorded yet.
            Once finance activity begins, accurate contract value, revenue, costs, and margin will appear here automatically.
          </p>
          <div className="mt-2 text-[10px] font-mono-alt uppercase tracking-widest text-slate-400">
            {data.project_code || ""} · {data.currency || "USD"}
          </div>
        </div>
      </section>
    );
  }

  const r = data.revenue || {};
  const c = data.costs || {};
  const m = data.margin || {};
  const l = data.linked || {};
  const marginTone = m.gross >= 0 ? "text-emerald-700" : "text-red-700";
  const budgetTone = m.budget_used_pct == null ? "text-slate-700"
    : m.budget_used_pct > 100 ? "text-red-700"
    : m.budget_used_pct > 85 ? "text-amber-700"
    : "text-emerald-700";
  const budgetBarTone = m.budget_used_pct == null ? "bg-slate-300"
    : m.budget_used_pct > 100 ? "bg-red-500"
    : m.budget_used_pct > 85 ? "bg-amber-500"
    : "bg-emerald-500";
  const budgetBarWidth = Math.min(100, Math.max(0, m.budget_used_pct || 0));

  return (
    <section className="border-t border-slate-100 px-5 py-4" data-testid={`pfs-panel-${projectId}`}>
      <div className="mb-3 flex items-baseline justify-between">
        <div>
          <div className="overline text-slate-500">Project Financial Snapshot</div>
          <div className="font-mono-alt text-[10px] uppercase tracking-widest text-slate-400">
            Cross-module live snapshot · pulled from invoices · finance · procurement · payroll · assets
          </div>
        </div>
        {data.budget > 0 && (
          <div className="text-right">
            <div className="overline text-slate-500">Budget</div>
            <div className="font-mono-alt tabular-nums text-sm font-semibold text-slate-900">{formatCurrency(data.budget)}</div>
          </div>
        )}
      </div>

      {/* Top KPI strip */}
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4 mb-4">
        <KpiTile
          icon={TrendUp} iconTone="text-emerald-600"
          label="Contract Value"
          value={formatCurrency(data.contract_value || 0)}
          sub={data.client}
          testid={`pfs-contract-${projectId}`}
        />
        <KpiTile
          icon={Receipt} iconTone="text-emerald-600"
          label="Revenue Invoiced"
          value={formatCurrency(r.invoiced || 0)}
          sub={`${formatCurrency(r.received || 0)} received`}
          testid={`pfs-invoiced-${projectId}`}
        />
        <KpiTile
          icon={CreditCard} iconTone="text-red-600"
          label="Total Costs"
          value={formatCurrency(c.total || 0)}
          sub={`${l.procurement_records || 0} proc · ${l.assets || 0} assets`}
          testid={`pfs-costs-${projectId}`}
        />
        <KpiTile
          icon={Coins} iconTone="text-blue-600"
          label="Gross Margin"
          value={formatCurrency(m.gross || 0)}
          sub={m.gross_pct != null ? `${m.gross_pct}%` : "—"}
          valueClass={marginTone}
          testid={`pfs-margin-${projectId}`}
        />
      </div>

      {/* Budget bar */}
      {data.budget > 0 && (
        <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50/50 px-4 py-3" data-testid={`pfs-budget-bar-${projectId}`}>
          <div className="flex items-center justify-between mb-1.5">
            <div className="overline">Budget utilisation</div>
            <div className={`font-mono-alt tabular-nums text-sm font-semibold ${budgetTone}`}>
              {m.budget_used_pct != null ? `${m.budget_used_pct}%` : "—"} of {formatCurrency(data.budget)}
            </div>
          </div>
          <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
            <div className={`h-full ${budgetBarTone} transition-all duration-500`} style={{ width: `${budgetBarWidth}%` }} />
          </div>
          <div className="mt-1.5 flex items-center justify-between text-[11px] text-slate-600">
            <span>Used: <span className="font-mono-alt font-semibold">{formatCurrency(c.total || 0)}</span></span>
            <span>Remaining: <span className={`font-mono-alt font-semibold ${m.overrun ? "text-red-700" : "text-emerald-700"}`}>{formatCurrency(m.budget_remaining || 0)}</span></span>
          </div>
          {m.overrun && (
            <div className="mt-2 rounded-md border border-red-200 bg-red-50/60 px-2 py-1 text-[11px] text-red-800" data-testid={`pfs-overrun-warn-${projectId}`}>
              <Warning size={10} weight="fill" className="inline mr-1" />
              This project is over budget. Review costs immediately.
            </div>
          )}
        </div>
      )}

      {/* 2-column: Revenue breakdown · Cost breakdown */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="rounded-lg border border-emerald-100 bg-emerald-50/30 px-4 py-3" data-testid={`pfs-revenue-card-${projectId}`}>
          <div className="overline mb-2 flex items-center gap-1.5 text-emerald-700"><Receipt size={11} weight="fill" /> Revenue</div>
          <BreakRow label="Recognised" value={r.recognised} />
          <BreakRow label="Invoiced" value={r.invoiced} bold />
          <BreakRow label="Received" value={r.received} tone="text-emerald-700" />
          <BreakRow label="Outstanding (AR)" value={r.outstanding_ar} tone="text-amber-700" />
          <div className="mt-1 text-[10px] font-mono-alt uppercase tracking-wider text-slate-500">
            {r.invoice_count || 0} invoice{r.invoice_count === 1 ? "" : "s"}
          </div>
        </div>

        <div className="rounded-lg border border-red-100 bg-red-50/30 px-4 py-3" data-testid={`pfs-costs-card-${projectId}`}>
          <div className="overline mb-2 flex items-center gap-1.5 text-red-700"><CreditCard size={11} weight="fill" /> Costs</div>
          <BreakRow label="Direct (finance entries)" value={c.direct} />
          <BreakRow label="AP billed" value={c.ap_billed} tone="text-slate-800" />
          <BreakRow label="AP paid" value={c.ap_paid} tone="text-emerald-700" />
          <BreakRow label="Procurement" value={c.procurement} />
          <BreakRow label="Payroll" value={c.payroll} />
          <BreakRow label="Assets" value={c.assets} />
          <BreakRow label="Overhead" value={c.overhead} />
          <BreakRow label="Total" value={c.total} bold divider tone="text-red-700" />
        </div>
      </div>

      {/* Linked records strip */}
      <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px]" data-testid={`pfs-linked-${projectId}`}>
        <span className="font-mono-alt uppercase tracking-wider text-slate-500">Linked records ·</span>
        <Chip icon={FileText} value={l.invoices || 0} label="invoices" />
        <Chip icon={CreditCard} value={l.ap_bills || 0} label="ap bills" />
        <Chip icon={ShoppingBag} value={l.procurement_records || 0} label="procurement" />
        <Chip icon={Buildings} value={l.assets || 0} label="assets" />
        <Chip
          icon={Briefcase}
          value={l.pending_approvals || 0}
          label="pending approvals"
          tone={(l.pending_approvals || 0) > 0 ? "amber" : "slate"}
        />
        {l.unpaid_supplier > 0 && (
          <Chip icon={Bank} value={formatCurrency(l.unpaid_supplier)} label="unpaid suppliers" tone="amber" />
        )}
        {l.donor_reports > 0 && (
          <Chip icon={CheckCircle} value={l.donor_reports} label="donor reports" />
        )}
      </div>
    </section>
  );
}

function KpiTile({ icon: Icon, iconTone, label, value, sub, valueClass = "text-slate-900", testid }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2" data-testid={testid}>
      <div className="flex items-center gap-1.5">
        <Icon size={11} className={iconTone} weight="fill" />
        <span className="overline">{label}</span>
      </div>
      <div className={`mt-0.5 font-mono-alt tabular-nums font-semibold ${valueClass}`}>{value}</div>
      {sub && <div className="text-[10px] text-slate-500 truncate">{sub}</div>}
    </div>
  );
}

function BreakRow({ label, value, bold = false, divider = false, tone }) {
  return (
    <div className={`flex items-baseline justify-between gap-2 ${divider ? "border-t border-slate-200 pt-1.5 mt-1.5" : "py-0.5"}`}>
      <span className={`text-xs ${bold ? "font-semibold text-slate-900" : "text-slate-600"}`}>{label}</span>
      <span className={`font-mono-alt tabular-nums text-xs ${bold ? "font-bold" : ""} ${tone || "text-slate-900"}`}>
        {formatCurrency(value || 0)}
      </span>
    </div>
  );
}

function Chip({ icon: Icon, value, label, tone = "slate" }) {
  const TONE = {
    slate: "bg-slate-100 text-slate-700 border-slate-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
  }[tone] || "bg-slate-100 text-slate-700 border-slate-200";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 ${TONE}`}>
      {Icon && <Icon size={10} weight="fill" />}
      <span className="font-mono-alt tabular-nums font-semibold">{value}</span>
      <span className="text-[10px] uppercase tracking-wider">{label}</span>
    </span>
  );
}
